import type { VoidFunctionComponent } from 'react';

import Tooltip from '../../../generic/Tooltip';
import type { TeamWithAssociations } from '../../../../../shared/types/routes/teams';

import { TEAM_NAME_LENGTH_LIMIT } from './common';

type Props = {
  teams: Array<Pick<TeamWithAssociations, 'id' | 'name'>>;
  teamSelection: 'null' | Set<number>;
};

/**
 * When the user has selected team(s), the TeamSelectionMenu will display the selected team name(s).
 * Automatically handles overflow, unassigned, and all projects.
 *
 * @param {Props} props - The props for the component.
 * @param {TeamWithAssociations[]} props.teams - The teams to display.
 */
const TeamSelectionMenuSelected: VoidFunctionComponent<Props> = ({ teams, teamSelection }) => {
  //  Handle unassigned (projects without teams)
  if (teamSelection === 'null') {
    return <span>Unassigned projects</span>;
  }
  //  Handle no teams (all projects)
  if (teamSelection.size === 0) {
    return <span>All projects</span>;
  }

  // otherwise combine team names with a comma (and space)
  const selectedTeams = teams.filter((team) => teamSelection.has(team.id));
  const selectedTeamNames = selectedTeams.map((team) => team.name).join(', ');

  if (selectedTeamNames.length > TEAM_NAME_LENGTH_LIMIT) {
    // handle overflow of one (truncate with ellipsis) or more (just show "N teams") long team names
    const selectedTeamCount = selectedTeams.length;
    const selectedTeamLabel = selectedTeamCount === 1
      ? `${selectedTeamNames.substring(0, TEAM_NAME_LENGTH_LIMIT)}...`
      : `${selectedTeamCount} teams`;

    return (
      <Tooltip id="team-selection-menu-tooltip" tooltip={selectedTeamNames}>
        <span>{selectedTeamLabel}</span>
      </Tooltip>
    );
  }

  return (
    <span>
      {selectedTeamNames}
    </span>
  );
};

export default TeamSelectionMenuSelected;
